// Adapters: convert API-boundary DTOs into clean view-model types.
//
// The backend exposes the raw legacy IMS schema (inv_number, inv_customer,
// invoiceData.invoice.inv_*). The UI consumes the camelCase `Transaction`
// view-model. This file is the only place that knows about both shapes.

import type {CreditDocument, InvoiceDocument} from '@/api/types/api.types';
import type {
  Address,
  Transaction,
  TransactionDirection,
  TransactionLine,
  TransactionStatus,
  TransactionType,
} from './types';
import {DocumentStatus} from './types';

function parseNumeric(val: unknown): number | undefined {
  if (val == null) return undefined;
  const n = typeof val === 'number' ? val : parseFloat(String(val));
  return Number.isFinite(n) ? n : undefined;
}

const STATUS_VALUES = new Set<string>(Object.values(DocumentStatus));

function coerceStatus(raw: unknown): TransactionStatus {
  if (typeof raw !== 'string') return DocumentStatus.PENDING;
  const normalized = raw.toLowerCase();
  // Tolerate legacy uppercase/plural spellings still floating around in old
  // DynamoDB rows so they don't all collapse into Pending.
  const aliased: Record<string, DocumentStatus> = {
    processed: DocumentStatus.COMPLETED,
    transmitted: DocumentStatus.COMPLETED,
    cancelled: DocumentStatus.CANCELED,
  };
  if (aliased[normalized]) return aliased[normalized];
  if (STATUS_VALUES.has(normalized)) return normalized as DocumentStatus;
  return DocumentStatus.PENDING;
}

function inferDirection(docType?: string): TransactionDirection {
  // 850 (PO), 856 (ASN), 840 (RFQ), 820 (remittance), 852 (POS) ->
  // inbound from our customers. 810 (Invoice), 855, 843, 860 -> outbound.
  const inboundCodes = new Set(['850', '856', '840', '820', '852']);
  return docType && inboundCodes.has(docType) ? 'inbound' : 'outbound';
}

function inferType(docType?: string): TransactionType {
  if (!docType) return 'invoice';
  if (docType === '850' || docType === '855' || docType === '856')
    return 'order';
  // Credits surface via their own endpoint; default invoice for the /invoices route
  return 'invoice';
}

type RawLine = Record<string, string | number | null | undefined>;
type RawAddress = Record<string, string | number | null | undefined>;

function toLines(raw: RawLine[] | undefined): TransactionLine[] | undefined {
  if (!raw || raw.length === 0) return undefined;
  return raw.map((l, i) => {
    const qty = parseNumeric(l.inv_l_qty) ?? 0;
    const unitPrice = parseNumeric(l.inv_l_price) ?? 0;
    // inv_l_lgoods2 is the line goods total (extended price net of discount).
    // Fall back to qty*unitPrice when missing.
    const amount = parseNumeric(l.inv_l_lgoods2) ?? qty * unitPrice;
    return {
      line: parseNumeric(l.inv_l_line) ?? i + 1,
      description: String(l.inv_l_desc ?? ''),
      qty,
      unit: String(l.inv_l_unit ?? ''),
      unitPrice,
      total: amount,
      amount,
    };
  });
}

function toBillTo(a: RawAddress | undefined): Address | undefined {
  if (!a || !a.inv_a_name) return undefined;
  return {
    name: String(a.inv_a_name ?? ''),
    address1: a.inv_a_addr1 ? String(a.inv_a_addr1) : undefined,
    address2: a.inv_a_addr2 ? String(a.inv_a_addr2) : undefined,
    city: a.inv_a_addr4 ? String(a.inv_a_addr4) : undefined,
    state: a.inv_a_addr5 ? String(a.inv_a_addr5) : undefined,
    postalCode: a.inv_a_post ? String(a.inv_a_post) : undefined,
  };
}

function toShipTo(a: RawAddress | undefined): Address | undefined {
  if (!a) return undefined;
  // Delivery block lives on the same row as the billing address (inv_a_del*).
  // Only emit a ship-to when there's actually delivery data.
  const hasDelivery = a.inv_a_del1 || a.inv_a_del2 || a.inv_a_del5;
  if (!hasDelivery) return undefined;
  return {
    name: String(a.inv_a_name ?? ''),
    address1: a.inv_a_del1 ? String(a.inv_a_del1) : undefined,
    address2: a.inv_a_del2 ? String(a.inv_a_del2) : undefined,
    city: a.inv_a_del5 ? String(a.inv_a_del5) : undefined,
    state: a.inv_a_del6 ? String(a.inv_a_del6) : undefined,
    postalCode: a.inv_a_del7 ? String(a.inv_a_del7) : undefined,
  };
}

/**
 * Convert a backend Document envelope to a UI Transaction view-model.
 * Tolerates missing nested fields — the backend's `invoiceData` is untyped on
 * the OpenAPI side.
 */
export function toTransaction(doc: InvoiceDocument): Transaction {
  const data = doc.invoiceData as unknown as {
    invoice?: Record<string, string>;
    invoiceLines?: RawLine[];
    invoiceAddresses?: RawAddress[];
  };
  const inv = data?.invoice ?? {};
  const addr = data?.invoiceAddresses?.[0];

  const number = doc.inv_number ?? inv.inv_number ?? '';
  const id = doc.id ?? `${number}#${doc.location ?? ''}#${doc.groupDb ?? ''}`;
  const docType = inv.inv_type ?? inv.inv_doctype ?? '';
  const rawDate = inv.inv_date ?? doc.created ?? doc.modified ?? '';
  // Supplier lives on each line, not the header — inv_agent is almost always
  // 0. Surface the first line's supplier as the vendor code.
  const supplierLine = data?.invoiceLines?.find(l => l.inv_l_supplier != null);
  const supplier = supplierLine?.inv_l_supplier;

  return {
    id,
    number,
    location: doc.location ?? '',
    groupDb: doc.groupDb ?? '',
    modified: doc.modified ?? '',
    rawInvoiceData: doc.invoiceData,
    direction: inferDirection(docType),
    status: coerceStatus(doc.status ?? inv.inv_status),
    type: inferType(docType),
    docType,
    tradingPartner: inv.inv_customer ?? doc.provider ?? '—',
    tradingPartnerId: doc.provider ?? '',
    date: rawDate,
    amount: parseNumeric(inv.inv_tgoods2 ?? inv.inv_tgoods1),
    currency: 'USD',
    poNumber: inv.inv_order,
    vendorCode: supplier != null ? String(supplier) : inv.inv_agent,
    billFrom: undefined,
    billTo: toBillTo(addr),
    shipTo: toShipTo(addr),
    remitTo: undefined,
    lines: toLines(data?.invoiceLines),
    events: undefined,
  };
}

export function toTransactions(
  docs: InvoiceDocument[] | undefined
): Transaction[] {
  if (!docs) return [];
  return docs.map(toTransaction);
}

export function toCreditTransaction(doc: CreditDocument): Transaction {
  const data = doc.creditData as unknown as {
    credit?: Record<string, string>;
    creditLines?: RawLine[];
  };
  const cre = data?.credit ?? {};

  const number = doc.cre_number ?? cre.cre_number ?? '';
  const id = doc.id ?? `${number}#${doc.location ?? ''}#${doc.groupDb ?? ''}`;
  const docType = cre.cre_type ?? cre.cre_doctype ?? '';
  const rawDate = cre.cre_date ?? doc.created ?? doc.modified ?? '';
  const supplierLine = data?.creditLines?.find(l => l.cre_l_supplier != null);
  const supplier = supplierLine?.cre_l_supplier;

  return {
    id,
    number,
    location: doc.location ?? '',
    groupDb: doc.groupDb ?? '',
    modified: doc.modified ?? '',
    rawInvoiceData: doc.creditData,
    direction: inferDirection(docType),
    status: coerceStatus(doc.status ?? cre.cre_status),
    type: 'credit',
    docType,
    tradingPartner: cre.cre_customer ?? doc.provider ?? '—',
    tradingPartnerId: doc.provider ?? '',
    date: rawDate,
    amount: parseNumeric(cre.cre_tgoods2 ?? cre.cre_tgoods1),
    currency: 'USD',
    poNumber: cre.cre_order,
    vendorCode: supplier != null ? String(supplier) : cre.cre_agent,
    billFrom: undefined,
    billTo: undefined,
    shipTo: undefined,
    remitTo: undefined,
    lines: undefined,
    events: undefined,
  };
}

export function toCreditTransactions(
  docs: CreditDocument[] | undefined
): Transaction[] {
  if (!docs) return [];
  return docs.map(toCreditTransaction);
}
